import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import { recurringTransactionService } from '../../services/recurringTransactionService';
import { transactionService } from '../../services/transactionService';
import { realtimeService } from '../../services/realtimeService';
import { Checkbox } from '../../components/ui/Checkbox';
import CompactBillCard from './components/CompactBillCard';
import CreateBillModal from './components/CreateBillModal';
import EditBillModal from './components/EditBillModal';
import FilterModal from './components/FilterModal';
import UpcomingView from './components/UpcomingView';
import CompactCalendarGrid from './components/CompactCalendarGrid';
import DayDetailsBottomSheet from './components/DayDetailsBottomSheet';


const BillsRecurring = () => {
  const navigate = useNavigate();
  const authContext = useAuth();
  
  // All hooks MUST be called before any conditional returns
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFrequency, setFilterFrequency] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showTransactionPrompt, setShowTransactionPrompt] = useState(false);
  const [selectedBillForTransaction, setSelectedBillForTransaction] = useState(null);
  const [dueBills, setDueBills] = useState([]);
  const [selectedDueBills, setSelectedDueBills] = useState([]);
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDaySheet, setShowDaySheet] = useState(false);
  const [monthPayments, setMonthPayments] = useState({});
  const [creatingBulk, setCreatingBulk] = useState(false);
  const [billTransactionStatus, setBillTransactionStatus] = useState({});

  // Extract user and loading from authContext safely
  const user = authContext?.user;
  const authLoading = authContext?.loading;

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadBills();
  }, [user, navigate]);

  // Real-time subscription for transactions
  useEffect(() => {
    if (!user) return;

    // Subscribe to transaction changes
    const channel = realtimeService?.subscribeToTransactions(
      (newTransaction) => {
        // When a new transaction is created, refresh bills to update the view
        console.log('New transaction detected, refreshing bills...');
        loadBills();
      },
      (updatedTransaction) => {
        // When a transaction is updated, refresh bills
        console.log('Transaction updated, refreshing bills...');
        loadBills();
      },
      (deletedTransaction) => {
        // When a transaction is deleted, refresh bills
        console.log('Transaction deleted, refreshing bills...');
        loadBills();
      }
    );

    // Cleanup subscription on unmount
    return () => {
      if (channel) {
        realtimeService?.unsubscribe(channel);
      }
    };
  }, [user]);

  useEffect(() => {
    if (activeTab === 'calendar') {
      generateMonthPayments();
    }
  }, [currentDate, bills, activeTab]);
  
  useEffect(() => {
    // Check for due bills and prompt for transaction creation
    checkDueBills();
  }, [bills, billTransactionStatus]);

  // NOW conditional returns can happen after all hooks
  if (!authContext || authLoading === undefined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Show loading state while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const loadBills = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await recurringTransactionService?.getAll();
      
      if (data && Array.isArray(data)) {
        setBills(data);
      } else {
        setBills([]);
      }
    } catch (err) {
      console.error('Error loading bills:', err);
      setError(err?.message || 'Failed to load bills');
      setBills([]);
    } finally {
      setLoading(false);
    }
  };
  
  const checkDueBills = async () => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      
      const billsDue = [];
      
      for (const bill of bills) {
        // Only check active bills with a next execution date
        if (bill?.status !== 'active' || !bill?.nextExecutionDate) continue;
        
        const nextDate = new Date(bill?.nextExecutionDate);
        nextDate?.setHours(0, 0, 0, 0);
        
        // Check if bill is due today or overdue (works for both income and expense)
        if (nextDate <= today) {
          // Use the transaction status from BillCard components
          const hasTransaction = billTransactionStatus?.[bill?.id] === true;
          
          // CRITICAL FIX: Only add to billsDue if NO transaction exists
          if (!hasTransaction) {
            billsDue?.push(bill);
          }
        }
      }
      
      // Show prompt if there are due bills without transactions
      if (billsDue?.length > 0) {
        setDueBills(billsDue);
        // Only reset selected bills if the list changed
        setSelectedDueBills(prev => prev?.filter(id => billsDue?.some(b => b?.id === id)));
        setShowTransactionPrompt(true);
      } else {
        // Close prompt if no bills are due
        setShowTransactionPrompt(false);
        setDueBills([]);
        setSelectedDueBills([]);
      }
    } catch (err) {
      console.error('Error checking due bills:', err);
    }
  };
  
  const handleBillTransactionStatusChange = (billId, hasTransaction) => {
    setBillTransactionStatus(prev => ({
      ...prev,
      [billId]: hasTransaction
    }));
  };
  
  const checkTransactionExists = async (bill, date) => {
    try {
      if (!bill?.accountId) {
        console.log(`[checkTransactionExists] No accountId for bill: ${bill?.transactionName}`);
        return false;
      }
      
      // Get all transactions for the bill's account
      const transactions = await transactionService?.getByAccount(bill?.accountId);
      
      if (!transactions || transactions?.length === 0) {
        console.log(`[checkTransactionExists] No transactions found for account: ${bill?.accountId}`);
        return false;
      }
      
      // Format the target date as YYYY-MM-DD for same-day comparison
      const targetDateStr = date?.toISOString()?.split('T')?.[0];
      
      // Get day of week (0 = Sunday, 6 = Saturday)
      const dayOfWeek = date?.getDay();
      
      // Build array of dates to check
      const datesToCheck = [targetDateStr];
      
      // If Saturday (6), also check Friday before
      if (dayOfWeek === 6) {
        const fridayBefore = new Date(date);
        fridayBefore?.setDate(fridayBefore?.getDate() - 1);
        datesToCheck?.push(fridayBefore?.toISOString()?.split('T')?.[0]);
      }
      
      // If Sunday (0), also check Friday before and Monday after
      if (dayOfWeek === 0) {
        const fridayBefore = new Date(date);
        fridayBefore?.setDate(fridayBefore?.getDate() - 2);
        datesToCheck?.push(fridayBefore?.toISOString()?.split('T')?.[0]);
        
        const mondayAfter = new Date(date);
        mondayAfter?.setDate(mondayAfter?.getDate() + 1);
        datesToCheck?.push(mondayAfter?.toISOString()?.split('T')?.[0]);
      }
      
      console.log('\n[checkTransactionExists] ========================================');
      console.log(`[checkTransactionExists] Checking bill: ${bill?.transactionName}`);
      console.log(`[checkTransactionExists] Bill ID: ${bill?.id}`);
      console.log(`[checkTransactionExists] Target Date: ${targetDateStr} (Day of week: ${dayOfWeek})`);
      console.log(`[checkTransactionExists] Dates to check: ${datesToCheck?.join(', ')}`);
      console.log(`[checkTransactionExists] Bill Type: ${bill?.type}`);
      console.log(`[checkTransactionExists] Bill Amount: ${bill?.amount}`);
      console.log(`[checkTransactionExists] Bill CategoryId: ${bill?.categoryId}`);
      console.log(`[checkTransactionExists] Total transactions in account: ${transactions?.length}`);
      
      // PRIMARY MATCHING: Check by recurring_transaction_id (most reliable)
      console.log('\n[checkTransactionExists] --- PRIMARY MATCH: Checking recurring_transaction_id ---');
      
      const matchByRecurringId = transactions?.find(txn => {
        const txnDateStr = txn?.date?.split('T')?.[0];
        
        console.log(`  Transaction: ${txn?.description}`);
        console.log(`    - ID: ${txn?.id}`);
        console.log(`    - recurringTransactionId: ${txn?.recurringTransactionId || 'NULL'}`);
        console.log(`    - Date: ${txnDateStr}`);
        console.log(`    - Type: ${txn?.type}`);
        console.log(`    - Amount: ${txn?.amount}`);
        
        // Must have recurring_transaction_id that matches this bill
        if (!txn?.recurringTransactionId) {
          console.log(`    ✗ No recurringTransactionId`);
          return false;
        }
        
        if (txn?.recurringTransactionId !== bill?.id) {
          console.log(`    ✗ recurringTransactionId mismatch: ${txn?.recurringTransactionId} !== ${bill?.id}`);
          return false;
        }
        
        // Must be on one of the target dates (exact date or weekend adjustment)
        const dateMatch = datesToCheck?.includes(txnDateStr);
        
        if (!dateMatch) {
          console.log(`    ✗ Date not in range: ${txnDateStr} not in [${datesToCheck?.join(', ')}]`);
          return false;
        }
        
        console.log(`    ✓✓✓ MATCH by recurringTransactionId!`);
        return true;
      });
      
      if (matchByRecurringId) {
        console.log('[checkTransactionExists] ✓ PRIMARY MATCH FOUND - Transaction exists');
        console.log('[checkTransactionExists] ========================================\n');
        return true;
      }
      
      console.log('[checkTransactionExists] No primary match found, trying fallback...');
      
      // FALLBACK MATCHING: If no recurring_transaction_id match, use comprehensive matching
      console.log('\n[checkTransactionExists] --- FALLBACK MATCH: Checking attributes ---');
      
      const matchByAttributes = transactions?.find(txn => {
        // Extract date from transaction
        const txnDateStr = txn?.date?.split('T')?.[0];
        
        console.log(`  Transaction: ${txn?.description}`);
        console.log(`    - Date: ${txnDateStr} (checking against: ${datesToCheck?.join(', ')})`);
        console.log(`    - AccountId: ${txn?.accountId} (target: ${bill?.accountId})`);
        console.log(`    - Type: ${txn?.type} (target: ${bill?.type})`);
        console.log(`    - Amount: ${txn?.amount} (target: ${bill?.amount})`);
        console.log(`    - CategoryId: ${txn?.categoryId} (target: ${bill?.categoryId})`);
        
        // Must match one of the target dates (exact date or weekend adjustment)
        if (!datesToCheck?.includes(txnDateStr)) {
          console.log(`    ✗ Date not in range`);
          return false;
        }
        
        // Must match account (already filtered, but double-check)
        if (txn?.accountId !== bill?.accountId) {
          console.log(`    ✗ Account mismatch`);
          return false;
        }
        
        // Must match type (income/expense) - NORMALIZE for comparison
        const billType = (bill?.type || '')?.toLowerCase()?.trim();
        const txnType = (txn?.type || '')?.toLowerCase()?.trim();
        
        if (txnType !== billType) {
          console.log(`    ✗ Type mismatch: "${txnType}" !== "${billType}"`);
          return false;
        }
        
        // Match by amount (use ABS to avoid sign mismatch, allow 0.01 tolerance)
        const billAmount = Math.abs(parseFloat(bill?.amount) || 0);
        const txnAmount = Math.abs(parseFloat(txn?.amount) || 0);
        const amountMatch = Math.abs(billAmount - txnAmount) < 0.01;
        
        if (!amountMatch) {
          console.log(`    ✗ Amount mismatch: |${billAmount}| vs |${txnAmount}|`);
          return false;
        }
        
        // Optional: Match by category if both have it
        if (bill?.categoryId && txn?.categoryId && bill?.categoryId !== txn?.categoryId) {
          console.log(`    ✗ Category mismatch`);
          return false;
        }
        
        console.log(`    ✓✓✓ MATCH by attributes!`);
        
        // All criteria matched
        return true;
      });
      
      if (matchByAttributes) {
        console.log('[checkTransactionExists] ✓ FALLBACK MATCH FOUND - Transaction exists');
      } else {
        console.log('[checkTransactionExists] ✗ NO MATCH FOUND - Transaction does NOT exist');
      }
      
      console.log('[checkTransactionExists] ========================================\n');
      
      return !!matchByAttributes;
    } catch (err) {
      console.error('Error checking transaction:', err);
      return false;
    }
  };
  
  const handleCreateTransactionForBill = () => {
    if (!selectedBillForTransaction) return;

    // Navigate to bill payment confirmation page with pre-filled data
    navigate('/bill-payment-confirmation', {
      state: {
        billData: {
          description: selectedBillForTransaction?.transactionName,
          amount: selectedBillForTransaction?.amount,
          type: selectedBillForTransaction?.type,
          categoryId: selectedBillForTransaction?.categoryId,
          date: selectedBillForTransaction?.nextExecutionDate,
          frequency: selectedBillForTransaction?.frequency
        },
        accountId: selectedBillForTransaction?.accountId
      }
    });
    
    setShowTransactionPrompt(false);
    setSelectedBillForTransaction(null);
  };
  
  const handleCreateTransactionsForDueBills = async () => {
    if (selectedDueBills?.length === 0) return;
    
    try {
      setCreatingBulk(true);
      const selectedBillsData = dueBills?.filter(bill => selectedDueBills?.includes(bill?.id));
      
      const results = [];
      const errors = [];
      
      // Create transactions for each selected bill
      for (const bill of selectedBillsData) {
        try {
          // Check one more time if transaction already exists (prevent duplicates)
          const nextDate = new Date(bill?.nextExecutionDate);
          nextDate?.setHours(0, 0, 0, 0);
          const alreadyExists = await checkTransactionExists(bill, nextDate);
          
          if (alreadyExists) {
            console.log(`Transaction already exists for ${bill?.transactionName}, skipping`);
            continue;
          }
          
          // CRITICAL: Validate and normalize the type
          const billType = bill?.type?.toLowerCase()?.trim();
          if (billType !== 'income' && billType !== 'expense' && billType !== 'transfer') {
            console.error(`[CREATE TRANSACTION] Invalid bill type: "${bill?.type}" for bill ${bill?.transactionName}`);
            errors?.push({ bill, error: `Invalid type: ${bill?.type}` });
            continue;
          }
          
          console.log(`[CREATE TRANSACTION] Creating transaction for bill: ${bill?.transactionName}`);
          console.log(`[CREATE TRANSACTION]   - Bill Type: "${billType}"`);
          console.log(`[CREATE TRANSACTION]   - Bill Amount: ${bill?.amount}`);
          console.log(`[CREATE TRANSACTION]   - Bill Category: ${bill?.category}`);
          console.log(`[CREATE TRANSACTION]   - Bill Account: ${bill?.account}`);
          
          const transactionData = {
            accountId: bill?.accountId,
            date: bill?.nextExecutionDate,
            description: bill?.transactionName,
            amount: parseFloat(bill?.amount),
            type: billType, // Use validated and normalized type
            categoryId: bill?.categoryId,
            cleared: false,
            notes: 'Created from Bills & Recurring',
            recurringTransactionId: bill?.id // CRITICAL: Link to recurring bill
          };
          
          console.log(`[CREATE TRANSACTION]   - Transaction Data:`, JSON.stringify(transactionData, null, 2));
          
          const created = await transactionService?.create(transactionData);
          
          console.log(`[CREATE TRANSACTION]   - Created Transaction:`, JSON.stringify(created, null, 2));
          console.log(`[CREATE TRANSACTION]   - Created Transaction Type: "${created?.type}"`);
          
          // VALIDATION: Verify the created transaction has the correct type
          if (created?.type !== billType) {
            console.error(`[CREATE TRANSACTION] ❌ TYPE MISMATCH DETECTED!`);
            console.error(`[CREATE TRANSACTION]   - Expected: "${billType}"`);
            console.error(`[CREATE TRANSACTION]   - Got: "${created?.type}"`);
            console.error(`[CREATE TRANSACTION]   - This indicates a database trigger or service layer issue!`);
          } else {
            console.log(`[CREATE TRANSACTION] ✅ Type validation passed: "${created?.type}" matches "${billType}"`);
          }
          
          results?.push({ bill, transaction: created });
          
          // Advance the bill's next execution date
          await advanceRecurringBill(bill);
        } catch (err) {
          console.error(`Error creating transaction for ${bill?.transactionName}:`, err);
          errors?.push({ bill, error: err?.message });
        }
      }
      
      // Close modal and refresh
      setShowTransactionPrompt(false);
      setDueBills([]);
      setSelectedDueBills([]);
      
      // Reload bills to get updated nextExecutionDate
      await loadBills();
      
      if (errors?.length > 0) {
        alert(`Created ${results?.length} transaction(s). Failed: ${errors?.length}`);
      } else {
        alert(`Successfully created ${results?.length} transaction(s)`);
      }
    } catch (err) {
      console.error('Error creating transactions:', err);
      alert('Failed to create transactions: ' + err?.message);
    } finally {
      setCreatingBulk(false);
    }
  };
  
  const advanceRecurringBill = async (bill) => {
    try {
      const currentNext = new Date(bill?.nextExecutionDate);
      let newNext = new Date(currentNext);
      
      const frequency = bill?.frequency?.toLowerCase();
      
      switch (frequency) {
        case 'daily':
          newNext?.setDate(newNext?.getDate() + 1);
          break;
        case 'weekly':
          newNext?.setDate(newNext?.getDate() + 7);
          break;
        case 'biweekly':
          newNext?.setDate(newNext?.getDate() + 14);
          break;
        case 'monthly':
          newNext?.setMonth(newNext?.getMonth() + 1);
          break;
        case 'quarterly':
          newNext?.setMonth(newNext?.getMonth() + 3);
          break;
        case 'yearly':
          newNext?.setFullYear(newNext?.getFullYear() + 1);
          break;
        default:
          console.warn(`Unknown frequency: ${frequency}`);
          return;
      }
      
      // Update the recurring bill with new next execution date
      await recurringTransactionService?.update(bill?.id, {
        nextExecutionDate: newNext?.toISOString()?.split('T')?.[0],
        lastExecutionDate: currentNext?.toISOString()?.split('T')?.[0]
      });
    } catch (err) {
      console.error('Error advancing recurring bill:', err);
    }
  };
  
  const handleSelectDueBill = (billId) => {
    setSelectedDueBills(prev => {
      if (prev?.includes(billId)) {
        return prev?.filter(id => id !== billId);
      }
      return [...prev, billId];
    });
  };
  
  const handleSelectAllDueBills = () => {
    if (selectedDueBills?.length === dueBills?.length) {
      setSelectedDueBills([]);
    } else {
      setSelectedDueBills(dueBills?.map(bill => bill?.id));
    }
  };

  const generateMonthPayments = () => {
    const payments = {};
    const year = currentDate?.getFullYear();
    const month = currentDate?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    bills?.forEach(bill => {
      if (bill?.status !== 'active') return;
      if (!bill?.nextExecutionDate) return;

      try {
        const occurrences = generateOccurrences(bill, firstDay, lastDay);
        occurrences?.forEach(date => {
          const dateKey = formatDateKey(date);
          if (!payments?.[dateKey]) {
            payments[dateKey] = [];
          }
          payments?.[dateKey]?.push({
            id: bill?.id,
            name: bill?.transactionName,
            amount: bill?.amount,
            type: bill?.type,
            category: bill?.category,
            categoryColor: bill?.categoryColor,
            categoryIcon: bill?.categoryIcon,
            frequency: bill?.frequency,
            isRecurring: true
          });
        });
      } catch (err) {
        console.error('Error generating payments for bill:', err);
      }
    });

    setMonthPayments(payments);
  };

  const generateOccurrences = (bill, startDate, endDate) => {
    const occurrences = [];
    
    // Validate bill and dates
    if (!bill?.nextExecutionDate || !startDate || !endDate) return occurrences;
    
    const nextDate = new Date(bill.nextExecutionDate);
    if (isNaN(nextDate?.getTime())) return occurrences;

    const frequency = bill?.frequency?.toLowerCase();
    if (!frequency) return occurrences;
    
    let currentDate = new Date(nextDate);

    while (currentDate <= endDate) {
      if (currentDate >= startDate && currentDate <= endDate) {
        occurrences?.push(new Date(currentDate));
      }

      // Increment date based on frequency - validate currentDate before mutation
      if (isNaN(currentDate?.getTime())) break;
      
      if (frequency === 'weekly') {
        currentDate?.setDate(currentDate?.getDate() + 7);
      } else if (frequency === 'biweekly') {
        currentDate?.setDate(currentDate?.getDate() + 14);
      } else if (frequency === 'monthly') {
        currentDate?.setMonth(currentDate?.getMonth() + 1);
      } else if (frequency === 'quarterly') {
        currentDate?.setMonth(currentDate?.getMonth() + 3);
      } else if (frequency === 'yearly') {
        currentDate?.setFullYear(currentDate?.getFullYear() + 1);
      } else {
        break;
      }

      // Safety check to prevent infinite loops
      if (isNaN(currentDate?.getTime())) break;
    }

    return occurrences;
  };

  const formatDateKey = (date) => {
    if (!date || isNaN(date?.getTime())) return '';
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    const day = String(date?.getDate())?.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleCreateBill = async (billData) => {
    try {
      await recurringTransactionService?.create(billData);
      setShowCreateModal(false);
      await loadBills();
    } catch (err) {
      console.error('Error creating bill:', err);
      throw err;
    }
  };

  const handleEditBill = (bill) => {
    setSelectedBill(bill);
    setShowEditModal(true);
  };

  const handleUpdateBill = async (billData) => {
    try {
      await recurringTransactionService?.update(selectedBill?.id, billData);
      setShowEditModal(false);
      setSelectedBill(null);
      await loadBills();
    } catch (err) {
      console.error('Error updating bill:', err);
      throw err;
    }
  };

  const handleDeleteBill = async (billId) => {
    if (!window.confirm('Are you sure you want to delete this bill?')) return;
    
    try {
      await recurringTransactionService?.delete(billId);
      await loadBills();
    } catch (err) {
      console.error('Error deleting bill:', err);
      alert('Failed to delete bill: ' + err?.message);
    }
  };

  const handleToggleStatus = async (bill) => {
    try {
      const newStatus = bill?.status === 'active' ? 'paused' : 'active';
      await recurringTransactionService?.update(bill?.id, { status: newStatus });
      await loadBills();
    } catch (err) {
      console.error('Error toggling bill status:', err);
      alert('Failed to update bill status: ' + err?.message);
    }
  };

  const handleFilterChange = (field, value) => {
    if (field === 'filterStatus') setFilterStatus(value);
    if (field === 'filterFrequency') setFilterFrequency(value);
    if (field === 'searchQuery') setSearchQuery(value);
  };

  const getFilteredBills = () => {
    if (!bills || !Array.isArray(bills)) return [];

    let filtered = bills;

    if (filterStatus !== 'all') {
      filtered = filtered?.filter(bill => bill?.status === filterStatus);
    }

    if (filterFrequency !== 'all') {
      filtered = filtered?.filter(bill => bill?.frequency === filterFrequency);
    }

    if (searchQuery && searchQuery?.trim() !== '') {
      const query = searchQuery?.toLowerCase()?.trim();
      filtered = filtered?.filter(bill => 
        (bill?.transactionName && bill?.transactionName?.toLowerCase()?.includes(query)) ||
        (bill?.description && bill?.description?.toLowerCase()?.includes(query)) ||
        (bill?.category && bill?.category?.toLowerCase()?.includes(query))
      );
    }

    return filtered;
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowDaySheet(true);
  };

  const getPaymentsForDate = (date) => {
    const dateKey = formatDateKey(date);
    return monthPayments?.[dateKey] || [];
  };

  const handlePrevMonth = () => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(newDate?.getMonth() - 1);
    if (!isNaN(newDate?.getTime())) {
      setCurrentDate(newDate);
    }
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(newDate?.getMonth() + 1);
    if (!isNaN(newDate?.getTime())) {
      setCurrentDate(newDate);
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getMonthYear = () => {
    try {
      return currentDate?.toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
      });
    } catch (err) {
      return '';
    }
  };

  const filteredBills = getFilteredBills();
  const activeFilterCount = (filterStatus !== 'all' ? 1 : 0) + (filterFrequency !== 'all' ? 1 : 0) + (searchQuery ? 1 : 0);

  if (!user) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Bills & Recurring - Rovyn Budget</title>
        <meta name="description" content="Manage your recurring bills and subscriptions" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-16 px-12 sm:px-16 md:px-24 max-w-[1400px] mx-auto">
          {/* Page Header */}
          <div className="mb-16 flex items-start justify-between gap-16">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-4">
                Bills & Recurring
              </h1>
              <p className="text-sm text-muted-foreground">
                Manage recurring bills and track upcoming payments
              </p>
            </div>
            <div className="flex items-center gap-8">
              <button
                onClick={() => setShowFilterModal(true)}
                className="flex items-center gap-8 px-12 py-8 bg-card border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="Filter" size={16} color="var(--color-foreground)" />
                <span className="text-sm font-medium text-foreground">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="px-6 py-2 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-8 px-12 py-8 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Icon name="Plus" size={16} color="currentColor" />
                <span className="text-sm font-medium">Create</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-8 mb-24 border-b border-border">
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`px-16 py-12 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'upcoming' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`px-16 py-12 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'list' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              List
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              className={`px-16 py-12 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'calendar' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Calendar
            </button>
          </div>

          {error && (
            <div className="mb-16 p-12 bg-error/10 border border-error/20 rounded-lg flex items-start gap-12">
              <Icon name="AlertCircle" size={18} color="var(--color-error)" />
              <div className="flex-1">
                <p className="text-sm font-medium text-error">{error}</p>
              </div>
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'upcoming' && (
            <UpcomingView 
              bills={getFilteredBills()} 
              onBulkTransactionsCreated={loadBills}
            />
          )}

          {activeTab === 'list' && (
            <div className="space-y-8">
              {getFilteredBills()?.length === 0 ? (
                <div className="text-center py-32">
                  <div className="w-64 h-64 mx-auto mb-16 bg-muted/30 rounded-full flex items-center justify-center">
                    <Icon name="Receipt" size={32} color="var(--color-muted-foreground)" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {searchQuery || filterStatus !== 'all' || filterFrequency !== 'all' ?'No bills match your filters' :'No recurring bills yet'}
                  </p>
                </div>
              ) : (
                getFilteredBills()?.map(bill => (
                  <CompactBillCard
                    key={bill?.id}
                    bill={bill}
                    onEdit={handleEditBill}
                    onDelete={handleDeleteBill}
                    onToggleStatus={handleToggleStatus}
                    onTransactionStatusChange={handleBillTransactionStatusChange}
                  />
                ))
              )}
            </div>
          )}

          {activeTab === 'calendar' && (
            <div className="space-y-16">
              {/* Calendar Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">
                  {getMonthYear()}
                </h2>
                <div className="flex items-center gap-8">
                  <button
                    onClick={handleToday}
                    className="px-10 py-6 text-xs font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
                  >
                    Today
                  </button>
                  <button
                    onClick={handlePrevMonth}
                    className="p-6 hover:bg-muted rounded-lg transition-colors"
                  >
                    <Icon name="ChevronLeft" size={18} color="var(--color-foreground)" />
                  </button>
                  <button
                    onClick={handleNextMonth}
                    className="p-6 hover:bg-muted rounded-lg transition-colors"
                  >
                    <Icon name="ChevronRight" size={18} color="var(--color-foreground)" />
                  </button>
                </div>
              </div>

              {/* Calendar Grid */}
              {loading ? (
                <div className="flex items-center justify-center py-32">
                  <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary" />
                </div>
              ) : (
                <CompactCalendarGrid
                  currentDate={currentDate}
                  monthPayments={monthPayments}
                  onDateClick={handleDateClick}
                />
              )}
            </div>
          )}
        </main>

        {/* Modals */}
        {showCreateModal && (
          <CreateBillModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateBill}
          />
        )}

        {showEditModal && selectedBill && (
          <EditBillModal
            bill={selectedBill}
            onClose={() => {
              setShowEditModal(false);
              setSelectedBill(null);
            }}
            onSubmit={handleUpdateBill}
          />
        )}

        <FilterModal
          isOpen={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          filters={{ filterStatus, filterFrequency, searchQuery }}
          onFilterChange={handleFilterChange}
        />

        <DayDetailsBottomSheet
          isOpen={showDaySheet}
          date={selectedDate}
          payments={getPaymentsForDate(selectedDate)}
          onClose={() => {
            setShowDaySheet(false);
            setSelectedDate(null);
          }}
        />

        {/* Transaction Prompt Modal */}
        {showTransactionPrompt && dueBills?.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
            <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-2xl max-h-[80vh] overflow-y-auto p-24">
              <div className="flex items-start gap-16 mb-20">
                <div className="flex-shrink-0 w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center">
                  <Icon name="AlertCircle" size={24} color="var(--color-warning)" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-card-foreground mb-8">
                    Bills & Income Due
                  </h3>
                  <p className="text-sm text-muted-foreground mb-16">
                    The following bills and income are due. Select which ones you'd like to create transactions for:
                  </p>
                </div>
              </div>
              
              {/* Select All Checkbox */}
              <div className="flex items-center gap-12 mb-16 pb-12 border-b border-border">
                <Checkbox
                  id="select-all-due"
                  checked={selectedDueBills?.length === dueBills?.length && dueBills?.length > 0}
                  onCheckedChange={handleSelectAllDueBills}
                />
                <label htmlFor="select-all-due" className="text-sm font-medium text-foreground cursor-pointer">
                  Select All ({dueBills?.length})
                </label>
              </div>
              
              {/* Due Bills List */}
              <div className="space-y-12 mb-20">
                {dueBills?.map(bill => {
                  const isSelected = selectedDueBills?.includes(bill?.id);
                  return (
                    <div 
                      key={bill?.id}
                      className={`bg-muted/50 rounded-lg p-16 flex items-start gap-12 cursor-pointer hover:bg-muted transition-colors ${
                        isSelected ? 'ring-2 ring-primary' : ''
                      }`}
                      onClick={() => handleSelectDueBill(bill?.id)}
                    >
                      <Checkbox
                        id={`due-bill-${bill?.id}`}
                        checked={isSelected}
                        onCheckedChange={() => handleSelectDueBill(bill?.id)}
                      />
                      <div 
                        className="w-36 h-36 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${bill?.categoryColor || '#6366f1'}20` }}
                      >
                        <Icon 
                          name={bill?.categoryIcon || 'Tag'} 
                          size={18} 
                          color={bill?.categoryColor || '#6366f1'} 
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-8 mb-4">
                          <h4 className="text-sm font-semibold text-foreground">{bill?.transactionName}</h4>
                          <span className="text-sm font-bold text-foreground">
                            ${bill?.amount?.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-center gap-8 text-xs text-muted-foreground">
                          <span className={`px-6 py-1 rounded-full ${
                            bill?.type === 'income' ? 'bg-success/10 text-success' : 
                            bill?.type === 'expense'? 'bg-error/10 text-error' : 'bg-primary/10 text-primary'
                          }`}>
                            {bill?.type}
                          </span>
                          <span>•</span>
                          <span>{bill?.category}</span>
                          <span>•</span>
                          <span>{bill?.account}</span>
                          <span>•</span>
                          <span>Due: {new Date(bill.nextExecutionDate)?.toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="flex gap-12">
                <button
                  onClick={() => {
                    setShowTransactionPrompt(false);
                    setDueBills([]);
                    setSelectedDueBills([]);
                  }}
                  className="flex-1 px-20 py-12 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth font-medium min-h-[44px]"
                >
                  Later
                </button>
                <button
                  onClick={handleCreateTransactionsForDueBills}
                  disabled={selectedDueBills?.length === 0 || creatingBulk}
                  className="flex-1 px-20 py-12 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingBulk ? 'Creating...' : `Create ${selectedDueBills?.length > 0 ? `${selectedDueBills?.length} ` : ''}Transaction${selectedDueBills?.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BillsRecurring;